export const cityTravelGuides = {
  "los-angeles": {
    travelGuideIntro:
      "Los Angeles rewards guests who plan by neighborhood rather than by city limits. A stay near the coast feels completely different from an Arts District food trip or a Hollywood-first weekend, so this guide helps Bellhopt guests and hosts recommend a sharper local itinerary.",
    popularAttractions: [
      {
        name: "Griffith Observatory",
        area: "Los Feliz And Griffith Park",
        detail:
          "A classic first-stop for skyline views, Hollywood Sign angles, and a quick feel for the scale of Los Angeles.",
      },
      {
        name: "Santa Monica Pier And Beach",
        area: "Santa Monica",
        detail:
          "An easy choice for ocean views, beach walks, and a postcard version of Southern California that still works for first-timers.",
      },
      {
        name: "The Getty Center",
        area: "Brentwood",
        detail:
          "A strong pick for guests who want architecture, gardens, and serious art without giving up one of the city's best hilltop settings.",
      },
      {
        name: "Universal Studios Hollywood",
        area: "Universal City",
        detail:
          "Best for travelers who want a blockbuster day with studio-tour energy and a very recognizable LA experience.",
      },
    ],
    restaurantAreas: [
      {
        area: "Arts District",
        vibe: "Creative Warehouses And Destination Dining",
        detail:
          "Best for dinner-led evenings built around gallery stops, cocktails, and some of the city's most talked-about tables.",
      },
      {
        area: "West Hollywood",
        vibe: "Polished Nights Out",
        detail:
          "A strong fit for design-forward travelers who want buzzy restaurants, late dinners, and easy access to the Sunset Strip.",
      },
      {
        area: "Santa Monica And Venice",
        vibe: "Coastal And Relaxed",
        detail:
          "Ideal for travelers who want beach energy, seafood, patios, brunch, and a less formal day-to-night rhythm.",
      },
    ],
    restaurantPicks: [
      {
        name: "Bestia",
        cuisine: "Italian",
        neighborhood: "Arts District",
        detail:
          "Still one of the clearest special-occasion reservations in LA for travelers who want an energetic room and a high-confidence dinner.",
      },
      {
        name: "Bavel",
        cuisine: "Middle Eastern",
        neighborhood: "Arts District",
        detail:
          "A strong shareable-menu choice when guests want bold flavors and one of downtown's most reliable group dinners.",
      },
      {
        name: "Musso & Frank Grill",
        cuisine: "Classic American",
        neighborhood: "Hollywood",
        detail:
          "An old-school Hollywood institution that works well for travelers who want timeless LA atmosphere with their dinner.",
      },
      {
        name: "Kato",
        cuisine: "Contemporary Taiwanese",
        neighborhood: "Downtown Los Angeles",
        detail:
          "A premium choice for guests who want a more ambitious tasting-style experience in a city known for destination dining.",
      },
    ],
    vibeGuide: [
      {
        vibe: "Classic Hollywood Weekend",
        area: "Hollywood And Los Feliz",
        note:
          "Best for first-time visitors who want landmark sightseeing, Griffith views, and an old-LA dinner reservation.",
      },
      {
        vibe: "Beach-First Reset",
        area: "Santa Monica And Venice",
        note:
          "Best for slower mornings, bike-path afternoons, and dinners where the ocean is part of the reason to go.",
      },
      {
        vibe: "Design-Forward Food Trip",
        area: "Arts District And West Hollywood",
        note:
          "Best for guests who care as much about galleries, cocktails, and interiors as they do about the restaurant itself.",
      },
    ],
    travelTips: [
      "Measure LA plans by neighborhood clusters, not by mileage. Crossing the city at the wrong time can turn a short map distance into most of an afternoon.",
      "Book headline restaurants early, especially in the Arts District and West Hollywood, where prime dinner times move quickly.",
      "Try to keep beach days and eastside or downtown plans on separate days so guests are not spending the trip in traffic.",
      "Even warm-weather stays should pack a light layer for coastal evenings, rooftops, and air-conditioned dining rooms.",
    ],
  },
  "san-diego": {
    travelGuideIntro:
      "San Diego stays are at their best when guests balance waterfront sightseeing with neighborhood dining instead of treating the city like one long beach strip. Bellhopt guests tend to get more out of the market when the itinerary matches the right part of town.",
    popularAttractions: [
      {
        name: "Balboa Park",
        area: "Central San Diego",
        detail:
          "A core San Diego stop for gardens, museums, architecture, and an easy half-day that suits both families and couples.",
      },
      {
        name: "San Diego Zoo",
        area: "Balboa Park",
        detail:
          "One of the city's signature attractions and still a high-value day plan for families or first-time visitors.",
      },
      {
        name: "La Jolla Cove",
        area: "La Jolla",
        detail:
          "A scenic coastal anchor for sea views, walking paths, and a more polished beach-town feel than the busier central shoreline.",
      },
      {
        name: "USS Midway Museum",
        area: "Embarcadero",
        detail:
          "A strong downtown attraction that pairs well with harbor walks and nearby dining in Little Italy.",
      },
    ],
    restaurantAreas: [
      {
        area: "Little Italy",
        vibe: "Lively And Walkable",
        detail:
          "The easiest dining district for visitors who want concentration, energy, and a strong mix of date-night and group options.",
      },
      {
        area: "North Park",
        vibe: "Casual, Creative, And Beer Friendly",
        detail:
          "A good fit for guests who want a neighborhood feel with breweries, newer restaurants, and less tourist-heavy energy.",
      },
      {
        area: "La Jolla",
        vibe: "Coastal And Upscale",
        detail:
          "Best for ocean-view lunches, polished dinners, and travelers who want a more refined beachside pace.",
      },
    ],
    restaurantPicks: [
      {
        name: "Juniper & Ivy",
        cuisine: "New American",
        neighborhood: "Little Italy",
        detail:
          "A dependable special-occasion reservation for guests who want one of San Diego's most established destination restaurants.",
      },
      {
        name: "Herb & Wood",
        cuisine: "Mediterranean-Inspired",
        neighborhood: "Little Italy",
        detail:
          "A polished dinner room that works especially well for couples, celebration dinners, and stylish group meals.",
      },
      {
        name: "Callie",
        cuisine: "Mediterranean",
        neighborhood: "East Village",
        detail:
          "A strong choice for travelers who want a modern downtown dinner with broad appeal and very consistent execution.",
      },
      {
        name: "George's At The Cove",
        cuisine: "California",
        neighborhood: "La Jolla",
        detail:
          "Best when the priority is pairing a scenic La Jolla outing with one of the city's classic coastal dining rooms.",
      },
    ],
    vibeGuide: [
      {
        vibe: "Waterfront City Break",
        area: "Embarcadero And Little Italy",
        note:
          "Best for harbor walks, museum stops, and easy dinner planning without long transfers around the county.",
      },
      {
        vibe: "Beachy Family Escape",
        area: "La Jolla And The Coast",
        note:
          "Best for guests who want scenic mornings, tide-pool energy, and dining that still feels polished enough for vacation.",
      },
      {
        vibe: "Casual Local Weekend",
        area: "North Park And South Park",
        note:
          "Best for travelers who want breweries, neighborhood coffee, and a more lived-in side of San Diego.",
      },
    ],
    travelTips: [
      "Start big outdoor plans earlier in the day, especially for the zoo, beaches, and Balboa Park walking time.",
      "Coastal parking can become the hardest part of the day in La Jolla and beach districts, so build in time or use ride-share.",
      "A marine layer can make mornings look gray even when the afternoon turns bright, so guests should not overreact to the forecast early.",
      "Little Italy and the Embarcadero combine well on the same day and make for one of the easiest first-day itineraries in the city.",
    ],
  },
  miami: {
    travelGuideIntro:
      "Miami works best when guests choose the right version of the city for their trip. Some stays want beach glamour and nightlife, while others want art, Cuban food, or a slower Coconut Grove pace, so the strongest guest guidance is neighborhood-specific.",
    popularAttractions: [
      {
        name: "South Beach And The Art Deco District",
        area: "Miami Beach",
        detail:
          "The classic first-time Miami experience for architecture, beach time, and a high-energy people-watching scene.",
      },
      {
        name: "Wynwood Walls",
        area: "Wynwood",
        detail:
          "A reliable anchor for mural-hopping, coffee stops, and building a day around Miami's most photographed creative district.",
      },
      {
        name: "Little Havana",
        area: "Calle Ocho",
        detail:
          "The right pick for guests who want music, cigar shops, Cuban culture, and a more rooted cultural experience than the beach corridor.",
      },
      {
        name: "Vizcaya Museum And Gardens",
        area: "Coconut Grove Area",
        detail:
          "A better fit for guests who want elegance, history, and a quieter counterweight to Miami's louder neighborhoods.",
      },
    ],
    restaurantAreas: [
      {
        area: "South Beach",
        vibe: "Scene-Driven And Glamorous",
        detail:
          "Best for travelers who want high-energy dining, long lunches, and a classic Miami night-out backdrop.",
      },
      {
        area: "Wynwood",
        vibe: "Creative And Social",
        detail:
          "Works well for groups and couples who want murals, bars, and contemporary dining in one walkable zone.",
      },
      {
        area: "Coconut Grove",
        vibe: "Leafy And Slower Paced",
        detail:
          "A strong option for guests who want a more relaxed waterfront feel with polished dining and less club energy.",
      },
    ],
    restaurantPicks: [
      {
        name: "Joe's Stone Crab",
        cuisine: "Seafood",
        neighborhood: "South Beach",
        detail:
          "Still the classic Miami restaurant name guests ask about first, especially between October 15 and May 1 during stone crab season.",
      },
      {
        name: "Boia De",
        cuisine: "Contemporary American",
        neighborhood: "Little Haiti Area",
        detail:
          "A top choice for travelers who prioritize one standout dinner and do not mind planning ahead for it.",
      },
      {
        name: "Macchialina",
        cuisine: "Italian",
        neighborhood: "South Beach",
        detail:
          "A dependable pick for guests who want a serious dinner without the louder energy of some flashier Miami rooms.",
      },
      {
        name: "Zak The Baker",
        cuisine: "Bakery And Cafe",
        neighborhood: "Wynwood",
        detail:
          "A strong breakfast or lunch stop when guests want something iconic but more casual than a full dinner reservation.",
      },
    ],
    vibeGuide: [
      {
        vibe: "Glam Beach Weekend",
        area: "South Beach And Mid-Beach",
        note:
          "Best for guests who want pool time, nightlife, statement dinners, and the version of Miami people picture first.",
      },
      {
        vibe: "Art And Food City Trip",
        area: "Wynwood And Design District",
        note:
          "Best for travelers who want murals, shopping, and restaurant-hopping without centering the whole trip on the sand.",
      },
      {
        vibe: "Tropical Slow Stay",
        area: "Coconut Grove And Vizcaya Area",
        note:
          "Best for couples and longer-stay travelers who want greenery, bayside movement, and a more relaxed pace.",
      },
    ],
    travelTips: [
      "Miami heat and humidity are real for much of the year, so guests should build in hydration, shade, and midday reset time.",
      "Popular restaurants and beach clubs can book out well ahead on weekends, especially in season, so advance reservations matter.",
      "If a trip mixes Miami Beach and mainland neighborhoods, cluster plans by area to avoid repeating bridge traffic several times a day.",
      "Guests visiting during stone crab season should know the dates are October 15 through May 1 if that experience is on the wish list.",
    ],
  },
  orlando: {
    travelGuideIntro:
      "Orlando is easy to undersell if the itinerary stops at theme parks. The city has strong neighborhood dining, calmer local districts, and better non-park options than many short-stay travelers realize, which makes a fuller local guide especially useful for family rentals.",
    popularAttractions: [
      {
        name: "Walt Disney World Resort",
        area: "Lake Buena Vista",
        detail:
          "The obvious anchor for family trips and one of the reasons many Orlando guests value a fully stocked kitchen before day one begins.",
      },
      {
        name: "Universal Orlando Resort",
        area: "International Drive Area",
        detail:
          "A top draw for multi-day trips where guests benefit from easy breakfasts, grab-and-go snacks, and late-night recovery food back at the rental.",
      },
      {
        name: "Lake Eola Park",
        area: "Downtown Orlando",
        detail:
          "A simple local reset for walking, skyline views, and a slower city moment away from the resort corridor.",
      },
      {
        name: "Winter Park Scenic Boat Tour",
        area: "Winter Park",
        detail:
          "A strong addition for guests who want something more local, scenic, and low-stress than another full park day.",
      },
    ],
    restaurantAreas: [
      {
        area: "Winter Park And Park Avenue",
        vibe: "Polished And Walkable",
        detail:
          "Best for visitors who want boutique shopping, brunch, and dinner in a refined district outside the parks.",
      },
      {
        area: "Mills 50",
        vibe: "Creative And Food Driven",
        detail:
          "One of the city's strongest local dining districts, especially for guests who want independent restaurants and street-art energy.",
      },
      {
        area: "Lake Nona",
        vibe: "Modern And Convenient",
        detail:
          "A good fit for travelers who want newer development, easier parking, and a more polished suburban dining mix.",
      },
    ],
    restaurantPicks: [
      {
        name: "The Ravenous Pig",
        cuisine: "Modern American",
        neighborhood: "Winter Park",
        detail:
          "A reliable top-tier dinner for guests who want one polished meal outside the resort corridor.",
      },
      {
        name: "Kaya",
        cuisine: "Filipino",
        neighborhood: "Mills 50",
        detail:
          "A strong choice for food-focused travelers who want one of Orlando's more distinctive restaurant experiences.",
      },
      {
        name: "Black Rooster Taqueria",
        cuisine: "Mexican",
        neighborhood: "Mills 50",
        detail:
          "A widely liked casual option that works well for groups, families, and guests wanting a lower-lift local meal.",
      },
      {
        name: "Sorekara",
        cuisine: "Japanese",
        neighborhood: "Baldwin Park Area",
        detail:
          "A premium reservation for guests who want a more ambitious dinner than the average theme-park trip usually plans around.",
      },
    ],
    vibeGuide: [
      {
        vibe: "Theme-Park Family Base",
        area: "Resort Corridor And Vacation Communities",
        note:
          "Best for families who need the home to work smoothly before and after long park days.",
      },
      {
        vibe: "Local Food And Art Stay",
        area: "Mills 50 And Winter Park",
        note:
          "Best for guests who want one or two park days balanced with better restaurants and more local character.",
      },
      {
        vibe: "Modern Orlando Weekend",
        area: "Lake Nona And Nearby Districts",
        note:
          "Best for travelers who care about convenience, newer development, and a less tourist-saturated setting.",
      },
    ],
    travelTips: [
      "Park itineraries are easier when breakfast, kid snacks, and hydration are already handled before the trip starts.",
      "Afternoon storms are common enough that guests should leave room to swap pool time and indoor plans around.",
      "Winter Park is one of the best choices for a non-park meal or half-day reset if the trip needs a slower pace.",
      "Driving times can look short on paper but still stretch with resort traffic, so avoid overscheduling more than one major destination in a day.",
    ],
  },
  phoenix: {
    travelGuideIntro:
      "Phoenix is strongest when guests treat it as more than a resort-and-golf market. The city has excellent neighborhood dining, real cultural institutions, and an outdoor rhythm built around early starts and late dinners, which should shape how hosts describe the stay.",
    popularAttractions: [
      {
        name: "Desert Botanical Garden",
        area: "Papago Park",
        detail:
          "One of the best first-day attractions for understanding the Sonoran landscape without committing to a long hike.",
      },
      {
        name: "Heard Museum",
        area: "Central Phoenix",
        detail:
          "A major cultural stop for guests who want context on Indigenous art and history in a city where place really matters.",
      },
      {
        name: "Camelback Mountain",
        area: "Camelback Corridor",
        detail:
          "A bucket-list outdoor plan for active travelers, best tackled early when the desert still feels manageable.",
      },
      {
        name: "Roosevelt Row",
        area: "Downtown Phoenix",
        detail:
          "The city's most obvious arts-and-food neighborhood for murals, coffee, and a more urban side of Phoenix.",
      },
    ],
    restaurantAreas: [
      {
        area: "Arcadia",
        vibe: "Lively And Comfortable",
        detail:
          "A strong all-around dining district for brunch, patios, and easy group decisions without downtown parking friction.",
      },
      {
        area: "Roosevelt Row And Downtown",
        vibe: "Art Forward And Urban",
        detail:
          "Best for travelers who want nightlife, murals, chef-driven restaurants, and a more current view of Phoenix.",
      },
      {
        area: "Biltmore",
        vibe: "Resort Polished",
        detail:
          "Best for business-leisure crossover trips, nicer lunches, and guests who want a cleaner luxury corridor.",
      },
    ],
    restaurantPicks: [
      {
        name: "Valentine",
        cuisine: "Southwestern",
        neighborhood: "Melrose Area",
        detail:
          "A defining Phoenix restaurant for guests who want cooking that feels grounded in the Sonoran Desert rather than generic upscale dining.",
      },
      {
        name: "Glai Baan",
        cuisine: "Thai",
        neighborhood: "Central Phoenix",
        detail:
          "A highly trusted local favorite for guests who want a casual room with destination-level food.",
      },
      {
        name: "Bacanora",
        cuisine: "Sonoran",
        neighborhood: "Grand Avenue Area",
        detail:
          "A strong pick when guests want something identifiably local and harder to replicate in another city.",
      },
      {
        name: "Farish House",
        cuisine: "Contemporary American",
        neighborhood: "Roosevelt Row Area",
        detail:
          "A polished option that fits couples and smaller groups looking for a more intimate dinner near downtown.",
      },
    ],
    vibeGuide: [
      {
        vibe: "Resort And Spa Stay",
        area: "Biltmore And Camelback Corridor",
        note:
          "Best for guests who want polished dinners, a calmer schedule, and a classic Phoenix leisure rhythm.",
      },
      {
        vibe: "Artsy Urban Weekend",
        area: "Roosevelt Row And Downtown",
        note:
          "Best for murals, breweries, galleries, and a younger dining scene with less resort gloss.",
      },
      {
        vibe: "Sunrise Activity Trip",
        area: "Arcadia And Papago Park",
        note:
          "Best for early hikes, brunch, and returning to the rental before the hottest part of the day hits.",
      },
    ],
    travelTips: [
      "Outdoor plans should start early. Phoenix heat can change the entire feel of a day by late morning.",
      "Hydration matters from the moment guests land, especially if the trip includes hiking, golf, or pool-heavy afternoons.",
      "Late dinners and evening patio time often make more sense than packing the middle of the day with activity.",
      "If guests want Camelback Mountain, they should treat it like a timed plan, not a casual add-on after breakfast.",
    ],
  },
  austin: {
    travelGuideIntro:
      "Austin delivers more when guests split their time between outdoor staples, neighborhood dining, and live-music pockets instead of treating downtown as the whole city. The strongest guest advice usually comes from pairing the right mood with the right side of town.",
    popularAttractions: [
      {
        name: "Barton Springs Pool",
        area: "Zilker Park",
        detail:
          "A signature Austin experience and one of the best ways for guests to feel the city's outdoors-first identity quickly.",
      },
      {
        name: "South Congress",
        area: "South Austin",
        detail:
          "The classic strip for shopping, coffee, murals, and a version of Austin that still feels iconic to visitors.",
      },
      {
        name: "Congress Avenue Bridge Bat Flight",
        area: "Downtown",
        detail:
          "A memorable dusk plan in warmer months and one of the easiest distinctly Austin experiences to recommend to first-timers.",
      },
      {
        name: "Texas State Capitol",
        area: "Downtown",
        detail:
          "A strong daytime stop for guests who want history and architecture without committing to a full museum-heavy itinerary.",
      },
    ],
    restaurantAreas: [
      {
        area: "South Congress",
        vibe: "Iconic And Walkable",
        detail:
          "Best for guests who want boutiques, patios, and restaurants layered into the same easy stretch of the city.",
      },
      {
        area: "East Austin",
        vibe: "Creative And Food Focused",
        detail:
          "The go-to district for barbecue, tacos, newer restaurants, and a more current local feel.",
      },
      {
        area: "Clarksville And Old West Austin",
        vibe: "Polished Neighborhood Dining",
        detail:
          "A good fit for travelers who want a calmer, more residential backdrop for a strong dinner reservation.",
      },
    ],
    restaurantPicks: [
      {
        name: "Franklin Barbecue",
        cuisine: "Texas Barbecue",
        neighborhood: "East Austin",
        detail:
          "Still the name many first-time food travelers build an Austin itinerary around, provided they plan the timing carefully.",
      },
      {
        name: "Suerte",
        cuisine: "Mexican",
        neighborhood: "East Austin",
        detail:
          "A dependable reservation for guests who want a stylish room and one of the city's most recognizable dinner spots.",
      },
      {
        name: "Emmer & Rye",
        cuisine: "Seasonal American",
        neighborhood: "Rainey Street Area",
        detail:
          "A strong pick for travelers who want a more refined, ingredient-driven Austin dinner.",
      },
      {
        name: "Matt's El Rancho",
        cuisine: "Tex-Mex",
        neighborhood: "South Austin",
        detail:
          "A classic choice for guests who want a more iconic, less formal Austin meal with broad group appeal.",
      },
    ],
    vibeGuide: [
      {
        vibe: "Live-Music Weekender",
        area: "South Congress And Downtown",
        note:
          "Best for guests who want easy bar hopping, street energy, and recognizable Austin landmarks in the mix.",
      },
      {
        vibe: "Eastside Food Crawl",
        area: "East Austin",
        note:
          "Best for barbecue, tacos, natural wine, and travelers who care more about restaurants than checklist sightseeing.",
      },
      {
        vibe: "Outdoors-First City Break",
        area: "Zilker, Barton Springs, And The Lake",
        note:
          "Best for guests who want mornings outside, afternoons back at the rental, and lighter evening plans.",
      },
    ],
    travelTips: [
      "Reserve in-demand dinners early and plan barbecue timing intentionally, because Austin still rewards guests who organize the food side of the trip.",
      "Heat and sun exposure add up quickly, so outdoor-heavy days work better with a mid-afternoon reset back at the property.",
      "South Congress is easiest to enjoy on foot, but parking and traffic around it can slow down a casual pop-in.",
      "In warmer months, the Congress Avenue Bridge bat flight is best treated as a dusk plan rather than something to squeeze in between dinner reservations.",
    ],
  },
  nashville: {
    travelGuideIntro:
      "Nashville can be a loud first impression, but the city has more range than Broadway alone. The best guest guidance balances classic live-music landmarks with neighborhood dining districts that give the stay more texture and a better food story.",
    popularAttractions: [
      {
        name: "Ryman Auditorium",
        area: "Downtown",
        detail:
          "A must for music history and one of the easiest ways to give first-time visitors a stronger sense of Nashville's identity.",
      },
      {
        name: "Country Music Hall Of Fame And Museum",
        area: "SoBro",
        detail:
          "A high-value daytime attraction that works especially well on arrival day or before a dinner reservation downtown.",
      },
      {
        name: "Broadway Honky-Tonks",
        area: "Downtown",
        detail:
          "Still the loudest and most recognizable version of Nashville, particularly for first-timers who want the full energy hit.",
      },
      {
        name: "Grand Ole Opry",
        area: "Opryland Area",
        detail:
          "A worthwhile add when guests want something more rooted in Nashville's music legacy than downtown nightlife alone.",
      },
    ],
    restaurantAreas: [
      {
        area: "12South",
        vibe: "Walkable And Stylish",
        detail:
          "Best for guests who want boutiques, coffee, and neighborhood dining with a polished but still approachable feel.",
      },
      {
        area: "Germantown",
        vibe: "Historic And Food Driven",
        detail:
          "A strong district for serious dinners, brunch, and a calmer pace than Broadway or the Gulch.",
      },
      {
        area: "East Nashville",
        vibe: "Creative And Local",
        detail:
          "Best for travelers who want inventive restaurants, bars with personality, and less tourist-heavy energy.",
      },
    ],
    restaurantPicks: [
      {
        name: "The Catbird Seat",
        cuisine: "Tasting Menu",
        neighborhood: "South Gulch",
        detail:
          "A premium reservation for guests building the trip around one high-end dining experience.",
      },
      {
        name: "Locust",
        cuisine: "Globally Influenced",
        neighborhood: "12South Area",
        detail:
          "A top pick for food-focused travelers who want one of Nashville's most in-demand and conversation-starting meals.",
      },
      {
        name: "Bastion",
        cuisine: "Contemporary American",
        neighborhood: "Wedgewood-Houston",
        detail:
          "A strong choice for guests who want creative cooking in a district that feels more local than tourist-centered.",
      },
      {
        name: "Hattie B's Hot Chicken",
        cuisine: "Nashville Hot Chicken",
        neighborhood: "Multiple Areas",
        detail:
          "A practical inclusion for first-time visitors who want to cover one essential Nashville food experience.",
      },
    ],
    vibeGuide: [
      {
        vibe: "First-Time Music City Trip",
        area: "Broadway And SoBro",
        note:
          "Best for guests who want easy access to headline attractions, rooftop bars, and late-night live music.",
      },
      {
        vibe: "Polished Weekend Away",
        area: "12South And The Gulch",
        note:
          "Best for boutique shopping, nicer dinners, and guests who want Nashville with a more curated tone.",
      },
      {
        vibe: "Creative Local Stay",
        area: "East Nashville And Wedgewood-Houston",
        note:
          "Best for travelers who want art spaces, inventive restaurants, and a side of the city that feels less expected.",
      },
    ],
    travelTips: [
      "Broadway is best experienced in a deliberate window, because too much time there can flatten the rest of what Nashville does well.",
      "Weekend reservations for top restaurants and rooftop bars move quickly, especially when there are major concerts or events in town.",
      "Downtown is often easier by ride-share than by driving and parking repeatedly through the core entertainment zones.",
      "Guests going to the Opry should treat it as a separate plan from Broadway night-out time rather than trying to force both into one compressed evening.",
    ],
  },
  "new-york": {
    travelGuideIntro:
      "New York City rewards guests who pick a lane for the day. A museum and park day, a downtown food crawl, and a Brooklyn waterfront afternoon each work well on their own, but the city feels rushed when travelers try to do every borough at once.",
    popularAttractions: [
      {
        name: "Central Park",
        area: "Manhattan",
        detail:
          "Still one of the city's most useful anchors because it pairs easily with museums, neighborhoods, and a slower first day.",
      },
      {
        name: "Statue Of Liberty And Ellis Island",
        area: "Lower Manhattan Harbor",
        detail:
          "A strong pick for first-time travelers who want one classic landmark day and are willing to commit the time to it.",
      },
      {
        name: "Brooklyn Bridge And DUMBO",
        area: "Lower Manhattan And Brooklyn",
        detail:
          "A high-return route for skyline views, walking, and getting one of the city's most photogenic transitions between neighborhoods.",
      },
      {
        name: "The Metropolitan Museum Of Art",
        area: "Upper East Side",
        detail:
          "A premium indoor plan that works especially well when the weather is rough or the trip needs a quieter half-day.",
      },
    ],
    restaurantAreas: [
      {
        area: "West Village",
        vibe: "Classic And Charming",
        detail:
          "Best for guests who want tree-lined blocks, high-demand restaurants, and the kind of New York walk that feels cinematic.",
      },
      {
        area: "Lower East Side",
        vibe: "Fast-Moving And Food Dense",
        detail:
          "A strong district for delis, bars, casual restaurant hopping, and guests who want a more kinetic downtown day.",
      },
      {
        area: "Williamsburg",
        vibe: "Brooklyn Cool",
        detail:
          "Works well for boutique shopping, cafe stops, waterfront views, and newer restaurant energy.",
      },
    ],
    restaurantPicks: [
      {
        name: "Katz's Delicatessen",
        cuisine: "Jewish Deli",
        neighborhood: "Lower East Side",
        detail:
          "An easy inclusion for first-time visitors who want one unmistakably New York meal with zero ambiguity about its status.",
      },
      {
        name: "Via Carota",
        cuisine: "Italian",
        neighborhood: "West Village",
        detail:
          "A classic West Village reservation for guests chasing a polished downtown dinner that still feels neighborhoody.",
      },
      {
        name: "Balthazar",
        cuisine: "French Brasserie",
        neighborhood: "SoHo",
        detail:
          "A reliable choice for travelers who want old-school New York buzz, whether for brunch, lunch, or dinner.",
      },
      {
        name: "Le Bernardin",
        cuisine: "Seafood",
        neighborhood: "Midtown",
        detail:
          "A premium flagship for guests building part of the trip around a world-class special-occasion meal.",
      },
    ],
    vibeGuide: [
      {
        vibe: "Iconic First-Timer Trip",
        area: "Midtown, Central Park, And Museum Mile",
        note:
          "Best for guests who want recognizable landmarks and a very direct New York experience without overcomplicating transit.",
      },
      {
        vibe: "Brownstone And Boutique Weekend",
        area: "West Village, SoHo, And Nolita",
        note:
          "Best for cafes, shopping, lower-scale streets, and travelers who want their trip to feel stylish rather than checklist heavy.",
      },
      {
        vibe: "Brooklyn Food And Views Stay",
        area: "Williamsburg And DUMBO",
        note:
          "Best for waterfront walks, newer restaurants, and guests who want some distance from Midtown intensity.",
      },
    ],
    travelTips: [
      "Keep daily plans geographically tight. New York is best explored in clusters rather than as one giant cross-city itinerary.",
      "Timed-entry tickets and headline restaurant reservations matter more here than in most cities, so advance planning has real payoff.",
      "Walking and the subway usually beat car service for daytime movement through Manhattan and nearby Brooklyn neighborhoods.",
      "Comfortable shoes matter more than almost any other packing decision because even a casual day can turn into several miles on foot.",
    ],
  },
  chicago: {
    travelGuideIntro:
      "Chicago is one of the easiest big cities to structure well when guests divide time between architecture, lakefront landmarks, and a few restaurant-heavy neighborhoods. The city feels much more generous when the itinerary respects both transit and weather.",
    popularAttractions: [
      {
        name: "Art Institute Of Chicago",
        area: "The Loop",
        detail:
          "A cornerstone attraction for guests who want one museum with genuine staying power and an easy downtown location.",
      },
      {
        name: "Chicago Architecture River Cruise",
        area: "Downtown",
        detail:
          "Still one of the highest-return activities in the city and one of the quickest ways to understand Chicago's scale and design.",
      },
      {
        name: "Millennium Park And Cloud Gate",
        area: "The Loop",
        detail:
          "A classic first stop that works well before the Art Institute, the Riverwalk, or a dinner reservation nearby.",
      },
      {
        name: "Wrigley Field",
        area: "Lakeview",
        detail:
          "A strong add for sports-minded travelers or anyone who wants one neighborhood outing with instant local atmosphere.",
      },
    ],
    restaurantAreas: [
      {
        area: "West Loop",
        vibe: "Chef-Driven And High Energy",
        detail:
          "The city's clearest dining destination for guests who want one reservation-packed, food-first neighborhood.",
      },
      {
        area: "River North",
        vibe: "Urban And Nightlife Friendly",
        detail:
          "A practical pick for travelers who want galleries, bars, and plenty of dinner options close to downtown hotels and attractions.",
      },
      {
        area: "Wicker Park",
        vibe: "Indie And Walkable",
        detail:
          "Best for guests who want shops, cocktail bars, coffee, and restaurants in a neighborhood that feels more local than central.",
      },
    ],
    restaurantPicks: [
      {
        name: "Smyth",
        cuisine: "Tasting Menu",
        neighborhood: "West Loop",
        detail:
          "A premium reservation for travelers who want one of Chicago's most decorated dining experiences.",
      },
      {
        name: "Kumiko",
        cuisine: "Japanese-Inspired",
        neighborhood: "West Loop",
        detail:
          "A standout choice for guests who care about both drinks and food and want a more singular Chicago night out.",
      },
      {
        name: "Monteverde",
        cuisine: "Italian",
        neighborhood: "West Loop",
        detail:
          "A strong all-around reservation when guests want one lively dinner with broad appeal and real polish.",
      },
      {
        name: "Au Cheval",
        cuisine: "Diner-Inspired American",
        neighborhood: "West Loop",
        detail:
          "A practical inclusion for first-time visitors who want one of the city's most famous casual meals.",
      },
    ],
    vibeGuide: [
      {
        vibe: "Architecture And Lakefront Trip",
        area: "The Loop And Riverwalk",
        note:
          "Best for first-timers who want Chicago's skyline, museums, and classic civic landmarks front and center.",
      },
      {
        vibe: "Chef-Driven Food Weekend",
        area: "West Loop And Fulton Market",
        note:
          "Best for guests who care most about dining and want the trip organized around restaurant reservations.",
      },
      {
        vibe: "Indie Neighborhood Stay",
        area: "Wicker Park And Logan Square",
        note:
          "Best for travelers who want record shops, cocktail bars, and a looser, more local pace.",
      },
    ],
    travelTips: [
      "Take the architecture cruise early in the trip if possible because it helps orient the rest of downtown and the river system.",
      "Chicago weather can change the feel of a day quickly, and lakefront wind can be much colder than guests expect from the forecast.",
      "Reserve top West Loop dinners well ahead on weekends, especially if the trip overlaps a convention or major sports schedule.",
      "The CTA is useful for moving between neighborhoods, but guests should still group plans by area to avoid spending too much of the day in transit.",
    ],
  },
  seattle: {
    travelGuideIntro:
      "Seattle works best when guests lean into neighborhoods instead of trying to treat the city like a single downtown core. Coffee, seafood, views, and nightlife all concentrate differently here, so a stronger page should help guests choose the version of Seattle that fits the stay.",
    popularAttractions: [
      {
        name: "Pike Place Market",
        area: "Downtown Waterfront",
        detail:
          "Still the city's defining stop for first-time visitors and one of the easiest ways to start a Seattle trip with food and views.",
      },
      {
        name: "Space Needle And Seattle Center",
        area: "Uptown",
        detail:
          "A strong single-area plan for skyline views, major attractions, and a very recognizable version of the city.",
      },
      {
        name: "Pioneer Square",
        area: "South Downtown",
        detail:
          "Best for guests who want history, brick architecture, and a more textured downtown walk than the waterfront alone.",
      },
      {
        name: "Ballard Locks",
        area: "Ballard",
        detail:
          "A worthwhile neighborhood attraction that gives travelers a more local side of Seattle's maritime personality.",
      },
    ],
    restaurantAreas: [
      {
        area: "Capitol Hill",
        vibe: "Energetic And Dense",
        detail:
          "The strongest all-around choice for guests who want nightlife, cafes, bars, and restaurant variety in one district.",
      },
      {
        area: "Ballard",
        vibe: "Maritime And Relaxed",
        detail:
          "Best for brewery stops, seafood, neighborhood shopping, and a slower pace than central Seattle.",
      },
      {
        area: "Pioneer Square",
        vibe: "Historic And Cocktail Friendly",
        detail:
          "A good fit for guests who want brick-lined streets, galleries, and dinner or drinks in a moodier historic setting.",
      },
    ],
    restaurantPicks: [
      {
        name: "Canlis",
        cuisine: "Fine Dining",
        neighborhood: "Queen Anne Area",
        detail:
          "A premium choice for guests who want a Seattle classic with sweeping views and a full special-occasion feel.",
      },
      {
        name: "Kamonegi",
        cuisine: "Japanese",
        neighborhood: "Fremont",
        detail:
          "A trusted food-forward reservation for travelers who want something more local and less traditional luxury-dining formal.",
      },
      {
        name: "Archipelago",
        cuisine: "Filipino",
        neighborhood: "Beacon Hill",
        detail:
          "A standout for guests building the trip around one of Seattle's most distinctive tasting experiences.",
      },
      {
        name: "Paju",
        cuisine: "Korean",
        neighborhood: "South Lake Union",
        detail:
          "A strong modern dinner option for guests who want a polished room without defaulting to classic hotel-district dining.",
      },
    ],
    vibeGuide: [
      {
        vibe: "Coffee-And-Walkable Stay",
        area: "Capitol Hill And Downtown",
        note:
          "Best for guests who want easy cafe access, nightlife, and enough density to do most of the trip without much planning friction.",
      },
      {
        vibe: "Maritime Neighborhood Weekend",
        area: "Ballard And The Waterfront",
        note:
          "Best for seafood, breweries, and a slower, distinctly Pacific Northwest pace.",
      },
      {
        vibe: "Historic Cocktail Trip",
        area: "Pioneer Square",
        note:
          "Best for brick architecture, galleries, and evening plans that feel moodier than the rest of the city.",
      },
    ],
    travelTips: [
      "Guests should pack layers and a rain shell even when the forecast looks manageable, because Seattle days can shift quickly.",
      "Pike Place Market is easiest early or later in the day when the densest visitor traffic is lighter.",
      "Seattle's hills can make short walks feel longer on the ground than they do on a map, especially with luggage or kids.",
      "Reservations matter for headline restaurants like Canlis and Archipelago, so food-first guests should not leave the city to chance.",
    ],
  },
  denver: {
    travelGuideIntro:
      "Denver travelers often split their time between city dining and mountain-adjacent plans, so the best local guidance helps them manage altitude, weather swings, and neighborhood selection. A stronger page should make the city feel intentional rather than generic stopover territory.",
    popularAttractions: [
      {
        name: "Red Rocks Park And Amphitheatre",
        area: "Morrison Area",
        detail:
          "The signature Denver excursion for views, live music, and an immediate sense of why the city is tied to the landscape around it.",
      },
      {
        name: "Denver Art Museum",
        area: "Golden Triangle",
        detail:
          "A high-value cultural stop for travelers who want one strong museum without leaving the urban core.",
      },
      {
        name: "Union Station",
        area: "LoDo",
        detail:
          "A practical first-day anchor because it connects well to downtown wandering, coffee, dining, and rail options.",
      },
      {
        name: "Meow Wolf Denver",
        area: "Sun Valley",
        detail:
          "A vivid indoor option that works well for mixed-age groups and travelers who want something more contemporary than classic sightseeing.",
      },
    ],
    restaurantAreas: [
      {
        area: "RiNo",
        vibe: "Artsy And High Energy",
        detail:
          "Best for breweries, murals, newer restaurants, and guests who want a more contemporary Denver feel.",
      },
      {
        area: "LoHi",
        vibe: "Food Driven And Neighborhood Scaled",
        detail:
          "A strong fit for travelers who want stylish dining and easy access to downtown without staying in the middle of it.",
      },
      {
        area: "Cherry Creek",
        vibe: "Polished And Upscale",
        detail:
          "Best for shopping, refined dinners, and guests who want a cleaner luxury corridor.",
      },
    ],
    restaurantPicks: [
      {
        name: "Tavernetta",
        cuisine: "Italian",
        neighborhood: "Union Station",
        detail:
          "A highly dependable downtown reservation for guests who want polish and an easy pairing with rail or hotel access.",
      },
      {
        name: "Safta",
        cuisine: "Middle Eastern",
        neighborhood: "RiNo",
        detail:
          "A go-to for travelers who want one of Denver's most cited dinner rooms without leaning too formal.",
      },
      {
        name: "Beckon",
        cuisine: "Tasting Menu",
        neighborhood: "RiNo",
        detail:
          "A premium choice for guests planning a true special-occasion dinner with a more intimate scale.",
      },
      {
        name: "Barolo Grill",
        cuisine: "Northern Italian",
        neighborhood: "Cherry Creek",
        detail:
          "A longstanding polished pick for guests who want a slower, classic fine-dining evening.",
      },
    ],
    vibeGuide: [
      {
        vibe: "Arts And Breweries Weekend",
        area: "RiNo",
        note:
          "Best for murals, more casual nightlife, and travelers who want the city's younger creative energy.",
      },
      {
        vibe: "Polished City Break",
        area: "Cherry Creek And LoDo",
        note:
          "Best for shopping, refined dinners, and a more elevated urban pace.",
      },
      {
        vibe: "City-Plus-Mountains Base",
        area: "LoHi And Union Station Area",
        note:
          "Best for guests who want the flexibility to split the trip between urban plans and scenic drives.",
      },
    ],
    travelTips: [
      "Altitude affects more visitors than they expect, so hydration and a lighter first day usually pay off.",
      "Weather can swing hard between morning and evening, even on clear days, so layers matter more than the forecast may suggest.",
      "Red Rocks deserves its own logistics plan for timing, transport, and post-show movement rather than being treated like a quick stop.",
      "Denver works best when guests avoid packing both a mountain excursion and a reservation-heavy city night into the same overlong day.",
    ],
  },
  charleston: {
    travelGuideIntro:
      "Charleston is strongest when guests lean into pace, place, and food instead of trying to speed through it. The city rewards walkable historic days, thoughtful dinner planning, and a clear sense of whether the trip is romantic, culinary, or coastal.",
    popularAttractions: [
      {
        name: "Rainbow Row And The Battery",
        area: "South Of Broad",
        detail:
          "The most recognizable Charleston walk for first-timers and one of the best ways to absorb the city's historic visual identity.",
      },
      {
        name: "Waterfront Park",
        area: "Historic District",
        detail:
          "An easy low-lift stop for harbor views, a scenic walk, and a calmer midday reset between meals or tours.",
      },
      {
        name: "Charleston City Market",
        area: "Historic District",
        detail:
          "A classic central stop for local crafts and a straightforward first-day orientation to the peninsula.",
      },
      {
        name: "Fort Sumter",
        area: "Charleston Harbor",
        detail:
          "A worthwhile add for guests who want history and a harbor outing rather than staying entirely within the old streets.",
      },
    ],
    restaurantAreas: [
      {
        area: "Upper King Street",
        vibe: "Lively And Social",
        detail:
          "Best for guests who want a walkable night out with stronger energy, bars, and plenty of dining choice.",
      },
      {
        area: "Cannonborough-Elliotborough",
        vibe: "Creative And Local",
        detail:
          "A strong neighborhood for travelers who want cafes, newer restaurants, and a more resident-driven feel.",
      },
      {
        area: "French Quarter And Historic District",
        vibe: "Classic Charleston",
        detail:
          "Best for guests who want old-city atmosphere, polished dining, and easy pairing with sightseeing.",
      },
    ],
    restaurantPicks: [
      {
        name: "FIG",
        cuisine: "Seasonal Southern",
        neighborhood: "Cannonborough",
        detail:
          "A classic Charleston reservation that still makes sense for guests who want one widely trusted dinner.",
      },
      {
        name: "Chubby Fish",
        cuisine: "Seafood",
        neighborhood: "Cannonborough",
        detail:
          "A highly sought-after choice for food-focused travelers who want one of the city's most talked-about meals.",
      },
      {
        name: "The Ordinary",
        cuisine: "Seafood And Oyster Hall",
        neighborhood: "Upper King",
        detail:
          "A strong fit for guests who want a larger, lively room with Charleston seafood energy and central access.",
      },
      {
        name: "Lewis Barbecue",
        cuisine: "Texas-Style Barbecue",
        neighborhood: "NoMo",
        detail:
          "A good option when guests want a more casual meal that still carries real destination status.",
      },
    ],
    vibeGuide: [
      {
        vibe: "Historic Charleston Escape",
        area: "South Of Broad And The French Quarter",
        note:
          "Best for romantic walks, classic architecture, and guests who want the postcard version of the city done well.",
      },
      {
        vibe: "Food-First Weekend",
        area: "Upper King And Cannonborough",
        note:
          "Best for travelers who want to organize the trip around restaurant reservations and coffee-to-cocktails walkability.",
      },
      {
        vibe: "Slow Southern Coastal Trip",
        area: "Historic District Plus Harbor Time",
        note:
          "Best for guests who want gentler pacing, more history, and room for one scenic outing beyond the central streets.",
      },
    ],
    travelTips: [
      "Reserve sought-after dinners early because Charleston's most in-demand restaurants stay busy well beyond peak holiday periods.",
      "The peninsula is very walkable, but cobblestones, humidity, and midday heat can make the city feel slower than the map suggests.",
      "Charleston is better when guests leave breathing room between meals and sightseeing rather than trying to stack every landmark into one day.",
      "A harbor or historic-site outing works especially well on a different day from the main restaurant night so the trip keeps some pace variety.",
    ],
  },
};
